<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { inputsStep2MX, inputsStep3MX, inputsStep4MX, inputsStep4Paqueteria, inputsStep4Maritimo } from './utils/inputsForm';
import { useToast } from '../../composables/useToast';
import { useMessage } from '../../composables/useMessage';
import {
  evaluateNames,
  validationRFC,
  wrongDepartureDateTime,
} from '../../helpers';
import {
  findClientByTributaryCode,
  findCompaniesGPS,
  findTypesContainers,
  findZipCode,
  createOrUpdateClient,
  createPolicy,
  createPolicyPackage,
  createPolicyMaritime,
  findCountries
} from '../../repositories/landing.repository';
import moment from 'moment';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const props = defineProps({
  dataForm1: {
    type: Object,
    required: true,
  },
});

const fieldsStep2: any = ref(inputsStep2MX());
const fieldsStep3: any = ref(inputsStep3MX());
const fieldsStep4: any = ref(inputsStep4MX());
const inputsHidden = ref<boolean>(true);
const dataForm: any = ref({});
const refFormClientData = ref<any>(null);
const loading = ref<boolean>(false);
const refFormTravelData = ref<any>(null);

const emit = defineEmits<{
  (e: 'firstView'): void;
}>();

onMounted(async () => {
  if (!props.dataForm1) {
    inputsHidden.value = true;
    return emit('firstView');
  }

  if(props.dataForm1.typePolicieTransport === 'Paqueteria'){
	fieldsStep4.value = inputsStep4Paqueteria();
  }
  if(props.dataForm1.typePolicieTransport === 'Maritimo'){
	fieldsStep4.value = inputsStep4Maritimo();
  }
});

onMounted(async () => {
  const comapaniesGPS = await findCompaniesGPS();
  const typesContainers = await findTypesContainers();
  const countries = await findCountries();
  
  fieldsStep3.value = fieldsStep3.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
    }
    return field;
  });
  fieldsStep4.value = fieldsStep4.value.map((field: any) => {
    if (field.key === 'containerType') {
      field.options = typesContainers;
    }
	if (field.key === 'countryDeparture') {
      field.options = countries;
    }
    if (field.key === 'countryArrival') {
      field.options = countries;
    }
    return field;
  });
});

onUnmounted(() => {
  fieldsStep3.value = inputsStep3MX().map((e) => (e.value = null));
  fieldsStep4.value = inputsStep4MX().map((e) => (e.value = null));
  fieldsStep4.value = inputsStep4Paqueteria().map((e) => (e.value = null));
  fieldsStep4.value = inputsStep4Maritimo().map((e) => (e.value = null));
  inputsHidden.value = true;
  dataForm.value = {};
});

const handleOnSubmitRFC = async (e: any) => {
  const isValidRfc = validationRFC(e.items.rfc);

  if (!e.isFormValid || !isValidRfc) {
    return (fieldsStep2.value[0].errors = ['rfc es invalido']);
  }

  const { locations, client } = await findClientByTributaryCode(e.items.rfc, 'MEXICO');

  if (client) {
    dataForm.value = { client };
  }

  if (locations && client) {
    const updatesClientInputs = [
      { key: 'tradeName', value: client.tradeName },
      { key: 'email', value: props?.dataForm1?.email ?? null },

      { key: 'phone1', value: client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: client.companyGPS?.id,
          nombre: client.companyGPS?.description,
        },
      },
      { key: 'address', value: client.address },
      { key: 'postCode', value: client.postCode },
      {
        key: 'colony',
        options: locations,
        value: {
          id: client.neighborhood.id,
          nombre: client.neighborhood.description,
        },
      },
      { key: 'city', value: client.city.description },
      { key: 'state', value: client.state.description },
    ];

    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);
  }

  const updatesTravelInputs = [
    { key: 'assetId', value: props?.dataForm1?.asset?.nombre ?? null },
    {
      key: 'indemnificationLimit',
      value: props?.dataForm1?.indemnificationLimit ?? null,
    },
    {
      key: 'indemnificationLimit-calc',
      value: `${props?.dataForm1?.endPrice} + IVA`,
    },
  ];

  updateFieldsInArray(fieldsStep4.value, updatesTravelInputs, null);
  inputsHidden.value = false;

  if (!client) {
    const updatesClientInputs = [
      { key: 'email', value: props?.dataForm1?.email ?? null },
    ];
    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);

    dataForm.value = { client: { rfc: e.items.rfc } };
  }
};

const handleChangeColony = async (e: any) => {
  const formProps = await refFormClientData.value.getValues();

  const location = await findZipCode(e.value, false);

  if (!location?.data?.length) return;

  const locations = location.data.map((loc: any) => ({
    nombre: loc.ColoniaDsc,
    id: loc.id,
  }));

  const preservedValues = {
    tradeName: formProps.items.tradeName,
    email: formProps.items.email,
    phone1: formProps.items.phone1,
    companyGPSId: formProps.items.companyGPSId,
    address: formProps.items.address,
  };

  updateFieldsInArray(
    fieldsStep3.value,
    [
      { key: 'colony', options: locations, value: { id: null } },
      { key: 'state', value: location.data[0].EstadoNombre },
      { key: 'city', value: location.data[0].MunicipioNombre },
      { key: 'postCode', value: location.data[0].code },
    ],
    preservedValues
  );

  dataForm.value.client = {
    ...dataForm.value.client,
    state: {
      id: location.data[0].EstadoID,
      description: location.data[0].EstadoNombre,
    },
    city: {
      id: location.data[0].MunicipioId,
      description: location.data[0].MunicipioNombre,
    },
    country: {
      id: location.data[0].PaisID,
      description: location.data[0].PaisNombre,
    },
  };
};

const handleChange = async (e: any) => {
  if(props.dataForm1.typePolicieTransport !== 'Terrestre') return;
  if (e.value.length < 4 || e.value.length > 7) return;

  const location = await findZipCode(e.value, false);

  // Si no existe el codigo postal dejamos una bandera en el input
  if (!location.data?.length) {
    if (e.input.key === 'sourceCityZipCodeId') {
      fieldsStep4.value.find(
        (field: any) => field.key === 'sourceCityZipCodeIdCalc'
      ).value = 'CP INVALIDO';
    } else {
      fieldsStep4.value.find(
        (field: any) => field.key === 'destinationCityZipCodeIdCalc'
      ).value = 'CP INVALIDO';
    }
    return;
  }

  // se estable el nombre de la locacion en el input
  if (e.input.key === 'sourceCityZipCodeId') {
    fieldsStep4.value.find(
      (field: any) => field.key === 'sourceCityZipCodeIdCalc'
    ).value = location.data[0].MunicipioNombre;
    dataForm.value.client = {
      ...dataForm.value.client,
      sourceCityZipCodeId: location.data[0].id,
    };
  } else {
    fieldsStep4.value.find(
      (field: any) => field.key === 'destinationCityZipCodeIdCalc'
    ).value = location.data[0].MunicipioNombre;
    dataForm.value.client = {
      ...dataForm.value.client,
      destinationCityZipCodeId: location.data[0].id,
    };
  }
};

const handleSelectChange = (e: any) => {
  const updateFields = [];

  if (e.input.key === 'box-trailer') {
    updateFields.push(
      { key: 'trailerPlate', disabled: !e.value.id },
      { key: 'trailerPlate', 'validations.rules.required': e.value.id }
    );
  } else {
    updateFields.push(
      { key: 'stop1', disabled: !e.value.id },
      { key: 'stop1', 'validations.rules.required': e.value.id },
      { key: 'stop2', disabled: !e.value.id },
      { key: 'stop3', disabled: !e.value.id }
    );
  }

  updateFieldsInArray(fieldsStep4.value, updateFields, null);
};

const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('value' in update) {
        field.value = update.value;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

const handleOnSubmitPolicies = ({ items, isFormValid }: any) => {
	if (props.dataForm1.typePolicieTransport === 'Paqueteria') {
	handleOnSubmitPoliciesPaqueteria({ items, isFormValid });
  } else if(props.dataForm1.typePolicieTransport === 'Terrestre'){
	handleOnSubmitPoliciesTerrestre({ items, isFormValid });
  }else{
	handleOnSubmitPoliciesMaritime({ items, isFormValid });
  }
}

const handleOnSubmitPoliciesTerrestre = async ({
  items: refFormTravelDataItems,
  isFormValid: refFormTravelDataValid,
}: any) => {
  const { items: refFormClientDataItems, isFormValid: refFormClientDataValid } =
    await refFormClientData.value.getValues();

  // Validación de campos requeridos
  if (!refFormClientDataValid) return;
  if (!refFormTravelDataValid) return;

  // Desestruccturacion de propiedades
  const {
    truckPlate,
    trailerPlate,
    driverName,
    documentDescription,
    driverCellphone,
    driverEmail,
    startDate,
    endDate,
    startTime,
    endTime,
    stops,
    stop1,
    stop2,
    stop3,
    destinationCityZipCodeIdCalc,
    sourceCityZipCodeIdCalc,
    document,
  } = refFormTravelDataItems;

  const { address, companyGPSId, email, phone1, tradeName, postCode, colony } =
    refFormClientDataItems;

  // Validación de datos
  const isOkDepartureDateTime = wrongDepartureDateTime(startDate, startTime, 'mx');

  if (isOkDepartureDateTime) {
    return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.la_hora_de_salida_no_puede_ser_menor'),
      'error'
    );
  }

  if (
    destinationCityZipCodeIdCalc === 'CP INVALIDO' ||
    sourceCityZipCodeIdCalc === 'CP INVALIDO'
  ) {
    return showMessage(t('nueva_operacion.nueva_contratacion_mx.codigo_postal_no_valido'), 'error');
  }

  const validationRfcAndName = evaluateNames(
    dataForm.value.client.rfc,
    tradeName
  );

  const { namesError, ...restEvaluateNames } = validationRfcAndName;

  if (namesError) {
    return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.ingrese_su_nombre_completo_seguido_de'),
      'error'
    );
  }


  const travelStartDate = new Date(`${startDate}T${startTime}`);
  const travelEndDate = new Date(`${endDate}T${endTime}`);
  const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  const start = moment(fechaISOstart);
  const end = moment(fechaISOend);
  if (end.isBefore(start)) {
	 return showMessage(
	 'La fecha de fin no puede ser menor a la de inicio'
    );
  }

  // Formato de la data para el request
  const createOrUpdateClientData = {
    ...restEvaluateNames,
    namesError,
    address,
    email,
    tradeName,
    phone1,
    countryId: dataForm.value.client.country.id,
    cityId: dataForm.value.client.city.id,
    rfc: dataForm.value.client.rfc,
    stateId: dataForm.value.client.state.id,
    neighborhoodId: colony.id,
    postCode: postCode,
    companyGPSId: companyGPSId.id,
    urlDomain: window.location.origin,
  };

  const assetId = props.dataForm1.asset.id;

  const createPolicyData = {
	rfc: dataForm.value.client.rfc,
	tradeName,
    truckPlate,
    trailerPlate,
    driverName,
    documentDescription,
    assetId,
    driverCellphone,
    driverEmail,
    travelStartDate: fechaISOstart,
    travelEndDate: fechaISOend,
    stop1,
    stop2,
    stop3,
    indemnificationLimit: props.dataForm1.indemnificationLimit,
    sourceCityZipCodeId: dataForm.value.client.sourceCityZipCodeId,
    destinationCityZipCodeId: dataForm.value.client.destinationCityZipCodeId,
    companyGPSId: companyGPSId.id,
    hasStops: stops.nombre,
    document: document?.length ? document[0].image : null,
	isAuth: true,
  };

  try {
    loading.value = true;

    // Request: 1 - si el cliente no existe lo crea, sino lo actualiza, 2 - crea la poliza
    const client = await createOrUpdateClient(
      dataForm.value.client.id,
      createOrUpdateClientData,
	  'MX'
    );

    const newPolicyId = await createPolicy(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData
    );
    loading.value = false;
    if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_mx.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
    loading.value = false;
    showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
  }
};

const handleOnSubmitPoliciesPaqueteria = async ({ items, isFormValid }: any) => {
	const { items: refFormClientDataItems, isFormValid: refFormClientDataValid } =
    await refFormClientData.value.getValues();

  if (!refFormClientDataValid) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.el_formulario_debe_estar_completo'),
      'error'
    );

  }
  if (!isFormValid) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.el_formulario_debe_estar_completo'),
      'error'
    );
  }

  const { address, companyGPSId, email, phone1, tradeName } = refFormClientDataItems;

  const {
    indemnificationLimit,
	packageNumbers
  } = items;

  const validationRfcAndName = evaluateNames(dataForm.value.client.rfc, tradeName);

  const {
    clientMotherSurname,
    clientFathersSurname,
    clientNames,
    clientBirthOrConstDate,
    taxIdVerificationCode,
    namesError,
    isClientAPhysicalPerson,
  } = validationRfcAndName;

  if (namesError) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.ingrese_su_nombre_completo_seguido_de'),
      'error'
    );
  }

  try {
    loading.value = true;

    const createOrUpdateClientData = {
      isClientAPhysicalPerson,
      clientMotherSurname,
      clientFathersSurname,
      clientNames,
      clientBirthOrConstDate,
      taxIdVerificationCode,
      namesError,
      address,
      email,
      tradeName,
      phone1,
      countryId: dataForm.value.client.country.id,
      cityId: dataForm.value.client.city.id,
      neighborhoodId: dataForm.value.client.neighborhood.id,
      postCode: dataForm.value.client.postCode,
      rfc: dataForm.value.client.rfc,
      stateId: dataForm.value.client.state.id,
      companyGPSId: dataForm.value.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
	  dataForm.value.client.id,
      createOrUpdateClientData,
      'MX',
    );
   	const packageNumbersClened = packageNumbers.split(',').map((e: string) => e.trim()).filter((e: string) => e !== '');

    const createPolicyData = {
      tradeName,
      rfc:  dataForm.value.client.rfc,
      indemnificationLimit,
      companyGPSId: companyGPSId.id,
      packageNumbers: JSON.stringify(packageNumbersClened),
	  isAuth: true,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
    );

    loading.value = false;

	if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_mx.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
	showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
    loading.value = false;
  }
};

const handleOnSubmitPoliciesMaritime = async ({ items, isFormValid }: any) => {
	const { items: refFormClientDataItems, isFormValid: refFormClientDataValid } =
    await refFormClientData.value.getValues();

  if (!refFormClientDataValid) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.el_formulario_debe_estar_completo'),
      'error'
    );
  }
  if (!isFormValid) {
    return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.el_formulario_debe_estar_completo'),
      'error'
    );
  }

  const { address, companyGPSId, email, phone1, tradeName } = refFormClientDataItems;

  const {
    indemnificationLimit,
    travelStartDate,
    travelEndDate,
    shippingCompany,
    destinationCityZipCodeIdCalc,
    sourceCityZipCodeIdCalc,
    containerType,
	countryDeparture,
	countryArrival,
    containerNumber,
    portDeparture,
    portArrival,
  } = items;

  if (
    destinationCityZipCodeIdCalc === 'CP INVALIDO' ||
    sourceCityZipCodeIdCalc === 'CP INVALIDO'
  ) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.codigo_postal_no_valido'),
      'error'
    );
  }

  const validationRfcAndName = evaluateNames(dataForm.value.client.rfc, tradeName);

  const {
    clientMotherSurname,
    clientFathersSurname,
    clientNames,
    clientBirthOrConstDate,
    taxIdVerificationCode,
    namesError,
    isClientAPhysicalPerson,
  } = validationRfcAndName;

  if (namesError) {
	return showMessage(
	  t('nueva_operacion.nueva_contratacion_mx.ingrese_su_nombre_completo_seguido_de'),
      'error'
    );
  }

  try {
    loading.value = true;

    const createOrUpdateClientData = {
      isClientAPhysicalPerson,
      clientMotherSurname,
      clientFathersSurname,
      clientNames,
      clientBirthOrConstDate,
      taxIdVerificationCode,
      namesError,
      address,
      email,
      tradeName,
      phone1,
      countryId: dataForm.value.client.country.id,
      cityId: dataForm.value.client.city.id,
      neighborhoodId: dataForm.value.client.neighborhood.id,
      postCode: dataForm.value.client.postCode,
      rfc: dataForm.value.client.rfc,
      stateId: dataForm.value.client.state.id,
      companyGPSId: dataForm.value.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
	  dataForm.value.client.id,
      createOrUpdateClientData,
      'MX',
    );

	const assetId = props.dataForm1.asset.id;

    const createPolicyData = {
      tradeName,
      rfc: dataForm.value.client.rfc,
      assetId,
      travelEndDate,
      travelStartDate,
      indemnificationLimit,
      companyGPSId: companyGPSId.id,
      shippingCompany,
      portDeparture,
      portArrival,
      typeContainerId: containerType.id,
	  countryIdDeparture: countryDeparture.id,
	  countryIdArrival: countryArrival.id,
      containerNumber,
	  isAuth: true,
    };

    const newPolicyId = await createPolicyMaritime(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
    );

    loading.value = false;

	if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_mx.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
	showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
    loading.value = false;
  }
};

const handleAllowEndDate = async (e: any) => {
	fieldsStep4.value.find(
    (field: any) => field.key === 'travelEndDate',
  ).disabled = e.value ? false : true;
  fieldsStep4.value.find((field: any) => field.key === 'travelEndDate').min =
    new Date(`${e.value}T00:00:00`);
  await refFormTravelData.value.setValue({ key: 'travelEndDate', value: null });
};
</script>

<template>
  <Layout :title="t('nueva_operacion.nueva_contratacion_mx.nueva_contratacion')" :textColor="'color-secondary'">
    <template #layout>
      <Form
        :inputs="fieldsStep2"
        @onSubmit="handleOnSubmitRFC"
        v-if="inputsHidden"
      >
        <template #buttons>
          <div class="d-flex">
            <CButton background="background-blue" style="font-size: 1rem">
              <template #content>{{ t('nueva_operacion.nueva_contratacion_mx.comprobar_rfc') }}</template>
            </CButton>
          </div>
        </template>
      </Form>
      <template v-if="!inputsHidden">
        <h4 class="color-pink">
		   {{ t('nueva_operacion.nueva_contratacion_mx.datos_del_cliente') }} (RFC: {{ dataForm?.client?.rfc ?? null }})
        </h4>
        <Form
          ref="refFormClientData"
          :inputs="fieldsStep3"
          @change="handleChangeColony"
        ></Form>
        <h4 class="my-3 color-pink">{{ t('nueva_operacion.nueva_contratacion_mx.datos_del_viaje') }}</h4>
        <Form
          ref="refFormTravelData"
          :inputs="fieldsStep4"
          @onSubmit="handleOnSubmitPolicies"
          @change="handleChange"
          @select="handleSelectChange"
		  @datePicker="handleAllowEndDate"
        >
          <template #buttons>
            <div class="my-2" v-if="messageHTML" v-html="messageHTML" />
            <CButton
              class="p-4 w-100 px-2"
              background="background-blue"
              style="font-size: 1.5rem"
            >
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{ t('nueva_operacion.nueva_contratacion_mx.registrar_poliza') }}</span>
                </div>
              </template>
            </CButton>
          </template>
        </Form>
      </template>
    </template>
  </Layout>
</template>
